export class AudioPlayerService {
  private sampleRate: number = 24000
  private bufferSize: number = 4096 // Reduced from 7680 for Safari iOS compatibility
  private audioQueue: Float32Array[] = []
  private isPlaying: boolean = false
  private isStreamComplete: boolean = false // Tracks if Gemini signaled stream end
  private scheduledTime: number = 0
  private initialBufferTime: number = 0.05 // Increased slightly to 50ms for Safari iOS stability
  private audioContext: AudioContext
  private gainNode: GainNode
  private checkInterval: number | null = null
  private endOfQueueAudioSource: AudioBufferSourceNode | null = null
  private nearEndCallbackTriggered: boolean = false // Prevent multiple near-end callbacks per message
  private playbackCompleteCallbackTriggered: boolean = false // Prevent multiple completion callbacks per message
  private allProcessedChunks: Float32Array[] = [] // Store all chunks for replay
  private timeoutIdForNextSchedule: number | null = null // For clearing scheduled buffer checks

  public onPlaybackComplete: () => void = () => {} // Called when queue is empty AND stream is complete
  public onPlaybackStarted: () => void = () => {} // Called when audio playback begins
  public onPlaybackNearEnd: () => void = () => {} // Called when 2 chunks remain (to start recording early)

  constructor(context: AudioContext, incomingSampleRate: number = 24000) {
    this.audioContext = context
    this.sampleRate = incomingSampleRate
    this.gainNode = this.audioContext.createGain()
    this.gainNode.connect(this.audioContext.destination)
  }

  private _base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  private _pcm16ToFloat32(chunk: Uint8Array): Float32Array {
    const float32Array = new Float32Array(chunk.length / 2)
    const dataView = new DataView(
      chunk.buffer,
      chunk.byteOffset,
      chunk.byteLength
    )
    for (let i = 0; i < float32Array.length; i++) {
      const int16 = dataView.getInt16(i * 2, true) // true for little-endian
      float32Array[i] = int16 / 32768
    }
    return float32Array
  }

  public addChunk(base64Pcm16Chunk: string) {
    // Reset the stream complete flag when a new chunk is added
    this.isStreamComplete = false
    // If playback was complete for a previous stream, ensure flags are reset for the new one.
    this.playbackCompleteCallbackTriggered = false

    const uint8Chunk = this._base64ToUint8Array(base64Pcm16Chunk)
    let processingBuffer = this._pcm16ToFloat32(uint8Chunk)

    // Process buffer into smaller chunks
    let tempProcessedForCurrentAdd: Float32Array[] = []
    while (processingBuffer.length >= this.bufferSize) {
      const bufferSlice = processingBuffer.slice(0, this.bufferSize)
      tempProcessedForCurrentAdd.push(bufferSlice)
      this.audioQueue.push(bufferSlice)
      processingBuffer = processingBuffer.slice(this.bufferSize)
    }
    // Add remaining buffer if not empty
    if (processingBuffer.length > 0) {
      tempProcessedForCurrentAdd.push(processingBuffer)
      this.audioQueue.push(processingBuffer)
    }
    this.allProcessedChunks.push(...tempProcessedForCurrentAdd)

    // example of how to auto-start playing
    // if (!this.isPlaying) {
    //   this.isPlaying = true
    //   // Initialize scheduledTime only when we start playing
    //   this.scheduledTime =
    //     this.audioContext.currentTime + this.initialBufferTime
    //   this.onPlaybackStarted() // Notify that playback started
    //   this.scheduleNextBuffer()
    // }
  }

  private createAudioBuffer(audioData: Float32Array): AudioBuffer {
    const audioBuffer = this.audioContext.createBuffer(
      1,
      audioData.length,
      this.sampleRate
    )
    audioBuffer.getChannelData(0).set(audioData)
    return audioBuffer
  }

  private scheduleNextBuffer() {
    const SCHEDULE_AHEAD_TIME = 0.2

    if (this.timeoutIdForNextSchedule) {
      clearTimeout(this.timeoutIdForNextSchedule)
      this.timeoutIdForNextSchedule = null
    }

    while (
      this.audioQueue.length > 0 &&
      this.scheduledTime < this.audioContext.currentTime + SCHEDULE_AHEAD_TIME
    ) {
      // Check if we're getting close to the end (2 chunks remaining)
      // Trigger early recording restart when we have 2 chunks left
      if (
        this.audioQueue.length <= 2 &&
        !this.nearEndCallbackTriggered &&
        this.isStreamComplete
      ) {
        //this.nearEndCallbackTriggered = true
        //this.onPlaybackNearEnd()
      }

      const audioData = this.audioQueue.shift()!

      // Skip empty buffers
      if (audioData.length === 0) {
        continue
      }

      const audioBuffer = this.createAudioBuffer(audioData)
      const source = this.audioContext.createBufferSource()

      // Track the last source for completion detection
      if (this.audioQueue.length === 0) {
        if (this.endOfQueueAudioSource) {
          this.endOfQueueAudioSource.onended = null
        }
        this.endOfQueueAudioSource = source
        source.onended = () => {
          if (
            this.audioQueue.length === 0 &&
            this.endOfQueueAudioSource === source
          ) {
            this.endOfQueueAudioSource = null
            if (
              this.isStreamComplete &&
              !this.playbackCompleteCallbackTriggered &&
              !this.nearEndCallbackTriggered
            ) {
              this.isPlaying = false
              this.playbackCompleteCallbackTriggered = true
              this.onPlaybackComplete()
              // Automatically reset for next message after completion callback
              this._resetForNextMessage()
            }
          }
        }
      }

      source.buffer = audioBuffer
      source.connect(this.gainNode)

      // Ensure we never schedule in the past
      const startTime = Math.max(
        this.scheduledTime,
        this.audioContext.currentTime
      )
      source.start(startTime)
      this.scheduledTime = startTime + audioBuffer.duration
    }

    if (this.audioQueue.length === 0) {
      if (this.isStreamComplete) {
        this.isPlaying = false
        if (this.checkInterval) {
          clearInterval(this.checkInterval)
          this.checkInterval = null
        }
      } else {
        // Set up interval to check for new audio data
        if (!this.checkInterval) {
          this.checkInterval = (window.setInterval(() => {
            if (this.audioQueue.length > 0) {
              this.scheduleNextBuffer()
            }
          }, 100) as unknown) as number
        }
      }
    } else {
      // Schedule next buffer check
      const nextCheckTime =
        (this.scheduledTime - this.audioContext.currentTime) * 1000
      this.timeoutIdForNextSchedule = window.setTimeout(
        () => this.scheduleNextBuffer(),
        Math.max(0, nextCheckTime - 50)
      )
    }
  }

  public async play() {
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
      } catch (err) {
        console.error('AudioPlayerService: Failed to resume AudioContext:', err)
        return
      }
    }

    if (this.isPlaying) {
      // If gain is 0 (paused), set it back to 1 and ensure scheduling continues
      if (this.gainNode.gain.value === 0) {
        this.gainNode.gain.setValueAtTime(1, this.audioContext.currentTime)
        this.isPlaying = true // Ensure isPlaying is true
        // Reset scheduledTime to ensure buffers are scheduled correctly from now
        this.scheduledTime =
          this.audioContext.currentTime + this.initialBufferTime
        this.scheduleNextBuffer() // Kickstart scheduling if it stopped
      }
      return
    }

    // Scenario 1: Replay a completed stream
    if (
      this.audioQueue.length === 0 &&
      this.isStreamComplete &&
      this.allProcessedChunks.length > 0
    ) {
      this.audioQueue = [...this.allProcessedChunks]
      // isStreamComplete remains true as the original stream *is* complete. We are playing a full buffer.
      this.playbackCompleteCallbackTriggered = false // Allow onPlaybackComplete to fire again
      this.nearEndCallbackTriggered = false // Reset for replay
      this.scheduledTime =
        this.audioContext.currentTime + this.initialBufferTime
      this.isPlaying = true
      this.gainNode.gain.setValueAtTime(1, this.audioContext.currentTime)
      this.onPlaybackStarted() // Signal start of this playback cycle
      this.scheduleNextBuffer()
      return
    }

    // Scenario 2: Start or Resume (if audioQueue has items)
    if (this.audioQueue.length > 0) {
      this.isPlaying = true
      this.gainNode.gain.setValueAtTime(1, this.audioContext.currentTime) // Ensure gain is 1

      // If scheduledTime is in the past (e.g. after a pause), reset it to start scheduling from now.
      if (this.scheduledTime <= this.audioContext.currentTime) {
        this.scheduledTime =
          this.audioContext.currentTime + this.initialBufferTime
      }

      // Only call onPlaybackStarted if it's not a resume from a gain-induced pause without stopping scheduling logic fully
      // A more explicit state like 'paused' vs 'stopped' would be better.
      // For now, assume if isPlaying was false and we're here, it's a start/resume that needs notification.
      this.onPlaybackStarted()
      this.scheduleNextBuffer()
      return
    }

    console.log(
      'AudioPlayerService: No audio data in queue to play, and not a replay scenario. Resetting for next message.'
    )
  }

  public pause() {
    if (!this.isPlaying) {
      console.log('AudioPlayerService: Not playing, ignoring pause() call')
      return
    }
    this.isPlaying = false
    this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime) // Mute via gain

    // Clear interval and timeout for scheduling new buffers
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
    if (this.timeoutIdForNextSchedule) {
      clearTimeout(this.timeoutIdForNextSchedule)
      this.timeoutIdForNextSchedule = null
    }
  }

  public stop() {
    this.isPlaying = false
    // isStreamComplete should reflect the actual stream state, stop() is a player action.
    // Let's not force isStreamComplete=true here unless it's part of reset().
    // User might stop a stream that's not yet complete.
    this.audioQueue = [] // Clear only the active queue, not allProcessedChunks
    this.scheduledTime = this.audioContext.currentTime

    // Clean up tracking properties
    if (this.endOfQueueAudioSource) {
      // Ensure we handle the onended logic if we manually stop
      this.endOfQueueAudioSource.onended = null // Prevent old onended from firing
      this.endOfQueueAudioSource.stop() // Stop the source node itself
      this.endOfQueueAudioSource = null
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
    if (this.timeoutIdForNextSchedule) {
      clearTimeout(this.timeoutIdForNextSchedule)
      this.timeoutIdForNextSchedule = null
    }

    // Fade out gain quickly
    this.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime)
    this.gainNode.gain.setValueAtTime(
      this.gainNode.gain.value, // Current gain (might be 0 if paused then stopped)
      this.audioContext.currentTime
    )
    this.gainNode.gain.linearRampToValueAtTime(
      0,
      this.audioContext.currentTime + 0.05
    )
  }

  public streamFinished() {
    // Called when Gemini signals no more audio chunks
    this.isStreamComplete = true

    // If queue is empty and we're not playing, trigger completion immediately
    if (
      this.audioQueue.length === 0 &&
      !this.isPlaying &&
      !this.playbackCompleteCallbackTriggered
    ) {
      this.playbackCompleteCallbackTriggered = true
      this.onPlaybackComplete()
      // Automatically reset for next message after completion callback
      this._resetForNextMessage()
    }
  }

  public getIsPlaying(): boolean {
    // isPlaying reflects intent and active audio output.
    // A non-empty queue while isPlaying is false means it's paused or ready to start.
    return this.isPlaying
  }

  private _resetForNextMessage() {
    // Internal reset for next message - called automatically after completion
    //console.log('AudioPlayerService: Auto-resetting for next message')
    this.nearEndCallbackTriggered = false // Reset near-end callback flag
    this.playbackCompleteCallbackTriggered = false // Reset completion callback flag

    // Reset gain to ensure clean start
    this.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime)
    this.gainNode.gain.setValueAtTime(1, this.audioContext.currentTime)
  }

  public reset() {
    // Reset the audio player service state for a new message
    //console.log(
    //  'AudioPlayerService: Resetting for new message (clears all chunks)'
    //)
    this.stop() // Stop current playback and clear active queue
    this.allProcessedChunks = [] // Clear all stored chunks for replay
    this._resetForNextMessage()
  }
}
